/**
 * Web Forgot Password Page
 *
 * Clean composition layer using the Template + Wrapper pattern.
 *
 * Architecture:
 * - Uses AuthLayoutTemplate for the two-column layout
 * - Uses ForgotPasswordTemplate for the form
 * - Handles authentication logic via authService
 * - Follows the same pattern as Login.tsx and Register.tsx
 *
 * Benefits:
 * - Minimal code (wrapper delegates to templates)
 * - Easy to test and maintain
 * - Reusable templates
 */

import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { AuthLayoutTemplate, ForgotPasswordTemplate, showToast } from '@ideoz/ui';
import { authService } from '@ideoz/core';

export default function ForgotPassword(): JSX.Element {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const [isLoading, setIsLoading] = useState(false);

    // Check if we came from auth dialog
    const fromAuthDialog = location.state?.from === 'authDialog';

    // Handle forgot password submission
    const handleSubmit = useCallback(
        async (email: string) => {
            setIsLoading(true);

            try {
                await authService.forgotPassword(email);
                showToast({
                    message: t('auth.resetInstructionsSent'),
                    type: 'success',
                });
                // Navigate back to login page after success
                setTimeout(() => {
                    navigate('/login');
                }, 2000);
            } catch (error: any) {
                // Show error toast
                showToast({
                    message: error.message || t('auth.sendResetFailed'),
                    type: 'error',
                });
                // Re-throw to let the form know submission failed (keeps email in field)
                throw error;
            } finally {
                setIsLoading(false);
            }
        },
        [navigate]
    );

    // Navigate back (to previous page - either login page or landing with dialog)
    const handleNavigateToLogin = useCallback(() => {
        if (fromAuthDialog) {
            // If came from auth dialog, go back and add a flag to reopen the dialog
            navigate('/?openDialog=login', { replace: true });
        } else {
            // Otherwise, navigate to login page
            navigate('/login');
        }
    }, [navigate, fromAuthDialog]);

    return (
        <AuthLayoutTemplate isWeb={true}>
            <ForgotPasswordTemplate
                onSubmit={handleSubmit}
                onNavigateToLogin={handleNavigateToLogin}
                isLoading={isLoading}
                isWeb={true}
            />
        </AuthLayoutTemplate>
    );
}
